import React, { useContext, useState, useRef, useEffect } from "react";
import { Container } from "../styled.ts";
import { Typography } from "../typography/index.tsx";
import { Tick } from "../icons/index.tsx";
import PaymentButton, { PaymentButtonRef } from "../payment/index.tsx";
import CouponModal from "../couponModal/index.tsx";
import { subscriberContext } from "../../context/subscriberContext.tsx";
import {
  BILLING_CYCLE,
  Plan,
  formatMoney,
  formatPlanName,
  getAnnualPrice,
} from "../../utils/pricing.ts";

interface SubscriptionTileProps {
  plan: Plan;
  /** The recommended card is the filled, gradient one. */
  isRecommended?: boolean;
}

interface AppliedCoupon {
  code: string;
  discount: number;
  discountType: "percentage" | "fixed";
  discountedAmount?: number;
}

const GRADIENT = "linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)";

function SubscriptionTile({
  plan,
  isRecommended = false,
}: SubscriptionTileProps) {
  const { subscriberDetails } = useContext(subscriberContext);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const paymentRef = useRef<PaymentButtonRef | null>(null);

  const catalogPrice = getAnnualPrice(plan);
  const price = getAnnualPrice(plan, appliedCoupon?.discountedAmount);

  // Reset coupon when the pricing basis changes (e.g. a new country catalog).
  useEffect(() => {
    setAppliedCoupon(null);
  }, [plan.amountMonth, plan.planName]);

  const handleApplyCoupon = (
    payload: AppliedCoupon | string | null
  ) => {
    if (!payload || (typeof payload === "string" && payload.trim() === "")) {
      setAppliedCoupon(null);
      return;
    }
    if (typeof payload === "string") {
      setAppliedCoupon({
        code: payload.toUpperCase(),
        discount: 10,
        discountType: "percentage",
      });
      return;
    }
    setAppliedCoupon({ ...payload, code: payload.code.toUpperCase() });
  };

  // One place to flip every colour between the light card and the gradient card.
  const ink = isRecommended ? "#fff" : "#202020";
  const muted = isRecommended ? "rgba(255, 255, 255, 0.78)" : "#828282";
  const hairline = isRecommended ? "rgba(255, 255, 255, 0.24)" : "#EDEDED";
  const chipBackground = isRecommended ? "rgba(255, 255, 255, 0.18)" : "#E8F0FF";
  const chipInk = isRecommended ? "#fff" : "#5395FF";

  return (
    <Container
      column
      gap="0"
      background={isRecommended ? GRADIENT : "#fff"}
      border={isRecommended ? "solid 1px transparent" : "solid 1px #EEE"}
      padding="28px 24px"
      radius="24px"
      width="100%"
      justifyContent="space-between"
      style={{
        boxSizing: "border-box",
        position: "relative",
        boxShadow: isRecommended
          ? "0 18px 40px rgba(83, 149, 255, 0.28)"
          : "0 2px 14px rgba(32, 32, 32, 0.05)",
      }}
    >
      <Container column gap="18px">
        {/* Plan identity */}
        <Container column gap="6px">
          {/* Reserved on every card so the price rows stay on one baseline */}
          <Container height="20px" alignItems="center">
            {isRecommended && (
              <Container
                background="rgba(255, 255, 255, 0.22)"
                padding="3px 10px"
                radius="100px"
                alignItems="center"
              >
                <Typography
                  tag="span"
                  fontFamily="Neue Montreal"
                  fontSize="11px"
                  fontWeight={500}
                  lineHeight="14px"
                  letterSpacing="0.06em"
                  color="#fff"
                >
                  MOST POPULAR
                </Typography>
              </Container>
            )}
          </Container>
          <Container alignItems="center" gap="8px">
            <Typography
              tag="h2"
              fontFamily="Neue Montreal"
              fontSize="20px"
              lineHeight="28px"
              letterSpacing="0.02em"
              fontWeight={500}
              margin="0"
              color={ink}
            >
              <Typography
                tag="span"
                fontWeight={500}
                fontStyle="italic"
                {...(isRecommended
                  ? { color: "#fff" }
                  : {
                      background: GRADIENT,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    })}
              >
                {formatPlanName(plan.planName)}
              </Typography>{" "}
              Plan
            </Typography>
          </Container>
          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
            letterSpacing="0.02em"
            margin="0"
            color={muted}
          >
            Track up to{" "}
            <Typography tag="span" fontWeight={600} color={ink}>
              {plan.noOfStocks} stocks
            </Typography>{" "}
            on WhatsApp
          </Typography>
        </Container>

        {/* Price - the monthly figure leads, the real charge is disclosed under it */}
        <Container column gap="8px">
          <Container alignItems="baseline" gap="8px" wrap>
            {price.strikeMonthly != null && (
              <Typography
                tag="span"
                fontFamily="Neue Montreal"
                fontSize="18px"
                fontWeight={400}
                lineHeight="24px"
                letterSpacing="0.02em"
                color={muted}
                textDecoration="line-through"
              >
                {formatMoney(price.strikeMonthly)}
              </Typography>
            )}
            <Typography
              tag="span"
              fontFamily="Neue Montreal"
              fontSize="44px"
              fontWeight={500}
              lineHeight="52px"
              letterSpacing="-0.01em"
              color={ink}
            >
              {formatMoney(price.headlineMonthly)}
            </Typography>
            <Typography
              tag="span"
              fontFamily="Neue Montreal"
              fontSize="15px"
              fontWeight={400}
              lineHeight="21px"
              letterSpacing="0.02em"
              color={muted}
            >
              /month
            </Typography>
          </Container>

          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize="13px"
            fontWeight={400}
            lineHeight="18px"
            letterSpacing="0.02em"
            margin="0"
            color={isRecommended ? "rgba(255, 255, 255, 0.9)" : "#5A5A5A"}
          >
            Billed{" "}
            <Typography tag="span" fontWeight={500} color={ink}>
              {formatMoney(price.billedTotal)}
            </Typography>{" "}
            once a year
          </Typography>

          {/* Savings, coupon state, or the nudge to annual - never more than one line */}
          {price.savings > 0 ? (
            <Container alignItems="center" gap="6px" wrap>
              <Container
                background={chipBackground}
                padding="4px 10px"
                radius="100px"
                alignItems="center"
              >
                <Typography
                  tag="span"
                  fontFamily="Neue Montreal"
                  fontSize="12px"
                  fontWeight={500}
                  lineHeight="16px"
                  letterSpacing="0.02em"
                  color={chipInk}
                >
                  You save {formatMoney(price.savings)} a year ·{" "}
                  {price.savingsPercent}% off
                </Typography>
              </Container>
              {appliedCoupon && (
                <Typography
                  tag="span"
                  fontFamily="Neue Montreal"
                  fontSize="12px"
                  fontWeight={500}
                  lineHeight="16px"
                  letterSpacing="0.02em"
                  color={chipInk}
                >
                  Coupon {appliedCoupon.code} applied
                </Typography>
              )}
            </Container>
          ) : (
            <Typography
              tag="span"
              fontFamily="Neue Montreal"
              fontSize="12px"
              fontWeight={400}
              lineHeight="16px"
              letterSpacing="0.02em"
              color={muted}
            >
              12 months of alerts, one payment
            </Typography>
          )}
        </Container>

        <Container height="1px" background={hairline} width="100%" />

        {/* What you get */}
        <Container column gap="12px">
          {plan.features.map((feature, index) => (
            <Container key={index} gap="10px" alignItems="center">
              <Container
                width="20px"
                height="20px"
                justifyContent="center"
                alignItems="center"
                radius="100px"
                background={isRecommended ? "rgba(255, 255, 255, 0.22)" : GRADIENT}
                style={{ flexShrink: 0 }}
              >
                <Tick color="#fff" width="12" height="12" />
              </Container>
              <Typography
                tag="span"
                fontFamily="Neue Montreal"
                fontSize="15px"
                fontWeight={400}
                lineHeight="21px"
                letterSpacing="0.02em"
                color={ink}
              >
                {feature.split("{}").map((part, partIndex, parts) => (
                  <React.Fragment key={partIndex}>
                    {part}
                    {partIndex < parts.length - 1 && (
                      <Typography tag="span" fontWeight={600} color={ink}>
                        {plan.noOfStocks}
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Typography>
            </Container>
          ))}
        </Container>
      </Container>

      {/* Commit */}
      <Container column gap="10px" margin="24px 0 0 0">
        <PaymentButton
          ref={paymentRef}
          tone={isRecommended ? "light" : "gradient"}
          planDetails={{
            name: plan.planName,
            billingCycle: BILLING_CYCLE,
            couponCode: appliedCoupon?.code,
          }}
        />
        {subscriberDetails && (
          <Container
            justifyContent="center"
            onClick={() => setIsCouponModalOpen(true)}
            style={{ cursor: "pointer" }}
          >
            <Typography
              tag="span"
              fontFamily="Neue Montreal"
              fontSize="12px"
              fontWeight={400}
              lineHeight="16px"
              cursor="pointer"
              color={isRecommended ? "#fff" : "#5395FF"}
              textDecoration="underline"
            >
              {appliedCoupon ? "Change coupon" : "Have a coupon code?"}
            </Typography>
          </Container>
        )}
      </Container>

      <CouponModal
        isOpen={isCouponModalOpen}
        onClose={() => setIsCouponModalOpen(false)}
        onApplyCoupon={handleApplyCoupon}
        planName={plan.planName}
        originalAmount={catalogPrice.billedTotal}
        appliedCoupon={appliedCoupon}
        billingCycle={BILLING_CYCLE}
        onProceedPayment={() => paymentRef.current?.openPayment()}
      />
    </Container>
  );
}

export default SubscriptionTile;
